import React, { useCallback, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import { Grid } from '@material-ui/core'
import SydButton from '../../../../../commonDesign/Button'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import { decipherError } from '../../../../../../utils/decipherError'
import { dataCatalog } from '../../../../../../api/coreData'
import SydSelect from '../../../../../commonDesign/SydSelect'
import SydLabel from '../../../../../commonDesign/SydLabel'
import { catalogIconMap, getIconUrl } from '../../shared/catalogIcons'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(1)
  },
  iconPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2)
  },
  iconImage: {
    width: 48,
    height: 48,
    objectFit: 'contain'
  },
  iconLabel: {
    fontSize: '1rem',
    fontWeight: 500
  },
  selectRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  fromSection: {
    flex: 1
  },
  toSection: {
    flex: 1
  }
}))

const useSubmitter = (dataSource, onComplete) => {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: changeDatasourceIcon } = dataCatalog.useChangeDatasourceIcon()

  const onSubmit = useCallback(async (formData) => {
    const command = {
      datasourceId: dataSource.catalogDatasourceId,
      icon: formData.icon
    }

    try {
      setError(null)
      setProcessing(true)

      const result = await changeDatasourceIcon(command)
      if (result?.statusCode === 500) {
        setError('Something unexpected happened while updating the datasource icon')
        return
      }

      onComplete(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [dataSource, onComplete, changeDatasourceIcon])

  return {
    onSubmit,
    processing,
    error
  }
}

function ChangeDatasourceIconForm ({ dataSource, onCancel, onComplete }) {
  const classes = useStyles()
  const [icon, setIcon] = useState(dataSource.icon || dataSource.type || 'table')
  const { onSubmit, processing, error } = useSubmitter(dataSource, onComplete)

  const iconOptions = useMemo(() => {
    return catalogIconMap.map(item => ({
      label: item.label,
      value: item.key
    }))
  }, [])

  const currentIconUrl = useMemo(() => {
    return getIconUrl({ icon })
  }, [icon])

  const originalIconUrl = useMemo(() => {
    return getIconUrl(dataSource)
  }, [dataSource])

  const currentIconLabel = useMemo(() => {
    const found = catalogIconMap.find(x => x.key === icon)
    return found?.label || icon
  }, [icon])

  if (!dataSource) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Alert severity='error'>No data source provided</Alert>
        </Grid>
      </Grid>
    )
  }

  return (
    <div className={classes.container}>
      <Grid container spacing={2}>
        {/* Icon Preview */}
        <Grid item xs={12}>
          <div className={classes.iconPreview}>
            <img src={currentIconUrl} alt={currentIconLabel} className={classes.iconImage} />
            <div className={classes.iconLabel}>{currentIconLabel}</div>
          </div>
        </Grid>

        {/* From/To Selection */}
        <Grid item xs={12}>
          <div className={classes.selectRow}>
            <div className={classes.fromSection}>
              <SydLabel label='Current Icon'>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <img src={originalIconUrl} alt='Current icon' style={{ width: 24, height: 24, objectFit: 'contain' }} />
                  <span>{catalogIconMap.find(x => x.key === (dataSource.icon || dataSource.type))?.label || dataSource.icon || dataSource.type}</span>
                </div>
              </SydLabel>
            </div>
            <div className={classes.toSection}>
              <SydLabel label='New Icon'>
                <SydSelect
                  options={iconOptions}
                  value={icon}
                  onChange={(e) => setIcon(e.target.value)}
                />
              </SydLabel>
            </div>
          </div>
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <SydModalActions>
            {error && <div className='__error'>{error}</div>}
            <SydButton
              disabled={processing}
              variant='ghost'
              size='md'
              onClick={onCancel}
            >
              Cancel
            </SydButton>
            <SydButton
              processing={processing}
              variant='primary'
              size='md'
              onClick={() => onSubmit({ icon })}
            >
              Save Changes
            </SydButton>
          </SydModalActions>
        </Grid>
      </Grid>
    </div>
  )
}

ChangeDatasourceIconForm.propTypes = {
  dataSource: PropTypes.shape({
    catalogDatasourceId: PropTypes.number.isRequired,
    displayName: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    subType: PropTypes.string.isRequired,
    icon: PropTypes.string,
    description: PropTypes.string,
    schema: PropTypes.shape({
      code: PropTypes.string
    })
  }).isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default ChangeDatasourceIconForm
